import type { AmbassadorBootstrap } from '@wix/ambassador-bootstrap-plugin';
import type { IHttpClient } from '@wix/http-client';
import type { QueryClient } from '@tanstack/react-query';
import { isSSR, createCacheKey } from '../utils';
import type { ErrorHandlerPublicAPI, ErrorCodesMap } from '@wix/error-handler';

export interface RequestOptions {
  errorCodesMap: ErrorCodesMap;
}

export abstract class RequestService<RpcModule, HttpModule> {
  private network: 'rpc' | 'http';

  constructor(
    private readonly rpcClient?: AmbassadorBootstrap,
    private readonly httpClient?: IHttpClient,
    private readonly queryClient?: QueryClient,
    private readonly errorHandler?: ErrorHandlerPublicAPI,
  ) {
    this.network = isSSR() ? 'rpc' : 'http';
  }

  protected abstract rpcModule(): Promise<RpcModule>;
  protected abstract httpModule(): Promise<HttpModule>;

  protected async request<TPayload, TResponse>({
    methodName,
    payload,
    options = {
      errorCodesMap: {},
    },
  }: {
    methodName: keyof (RpcModule & HttpModule);
    payload: TPayload;
    options?: RequestOptions;
  }): Promise<TResponse> {
    if (!isSSR() && this.queryClient) {
      const serviceMethodName = `${this.constructor.name}.${String(
        methodName,
      )}`;
      const cacheKey = createCacheKey(serviceMethodName, payload);

      return this.queryClient.fetchQuery({
        queryKey: cacheKey,
        queryFn: () => this.executeRequest(methodName, payload, options),
      });
    }

    return this.executeRequest(methodName, payload, options);
  }

  private async executeRequest<TPayload, TResponse>(
    methodName: keyof (RpcModule & HttpModule),
    payload: TPayload,
    options?: RequestOptions,
  ): Promise<TResponse> {
    if (this.network === 'rpc') {
      if (!this.rpcClient) {
        throw new Error('Error: rpcClient is undefined');
      }
      const rpcModule = await this.rpcModule();
      const method = rpcModule[methodName as keyof RpcModule];
      if (typeof method !== 'function') {
        throw new Error(
          `Method ${String(methodName)} not found in RpcModule for ${
            this.constructor.name
          } service.`,
        );
      }
      return this.rpcClient.request(method(payload)) as Promise<TResponse>;
    } else {
      if (!this.httpClient) {
        throw new Error('Error: httpClient is undefined');
      }
      const httpModule = await this.httpModule();
      const method = httpModule[methodName as keyof HttpModule];
      if (typeof method !== 'function') {
        throw new Error(
          `Method ${String(methodName)} not found in HttpModule for ${
            this.constructor.name
          } service.`,
        );
      }
      const requestFn = () => this.httpClient!.request(method(payload));

      if (this.errorHandler && options?.errorCodesMap) {
        const response = await this.errorHandler
          .withErrorHandler(requestFn, {
            errorCodesMap: options.errorCodesMap,
          })
          .catch((err) => {
            this.errorHandler?.getResolvedError(err);
            throw err;
          });
        return response.data as Promise<TResponse>;
      }

      const response = await requestFn();
      return response.data as Promise<TResponse>;
    }
  }
}
